/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { PaymentMethod, UnitOfMeasure } from 'utils/enums';
import { toastConfig } from 'utils/toastConfig';
import { TUser, TPaymentMethod, TSalesInvoiceItems } from 'utils/types';
import CreateSalesInvoiceInputs from './SalesInvoice/CreateSalesInvoiceInputs';
import CreateSalesInvoiceTable from './SalesInvoice/CreateSalesInvoiceTable';
import {
  buildUpsertSalesInvoiceItemPayload,
  mapApiSalesInvoiceItems
} from './SalesInvoice/salesInvoiceItemPayload';
import { useNavigate } from 'react-router-dom';

export type SalesInvoice = {
  soldTo: string | undefined;
  customerId: number | null;
  customerName?: string | null;
  invoicedOn: Date | string | null;
  invoiceNumber: string;
  remarks: string;
  paymentMethodId: number;
  paymentReference: string;
  salesStaffId: number | null;
  discountCode?: string;
};

const SalesInvoiceForm = () => {
  const { selectedBranch } = useBranchContext();
  const [salesInvoice, setSalesInvoice] = useState<SalesInvoice>({
    soldTo: 'Cash',
    customerId: null,
    customerName: '',
    invoicedOn: new Date(),
    invoiceNumber: '',
    remarks: '',
    paymentMethodId: PaymentMethod.Cash,
    paymentReference: '',
    salesStaffId: null,
    discountCode: ''
  });
  const [items, setItems] = useState<TSalesInvoiceItems[]>([
    {
      productId: 0,
      procurementInvoiceId: 0,
      quantity: 1,
      unitOfMeasure: UnitOfMeasure.Piece,
      unitPrice: 0,
      productName: '',
      total: 0,
      salesInvoiceItemId: 0,
      noIdProductName: '',
      id: '',
      isDiscountable: false
    }
  ]);
  const [paymentMethods, setPaymentMethods] = useState<TPaymentMethod[] | null>(
    null
  );
  const [salesStaff, setSalesStaff] = useState<TUser[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSalesInvoiceCreated, setIsSalesInvoiceCreated] = useState(false);
  const [salesInvoiceId, setSalesInvoiceId] = useState(0);
  const [uniqueCode, setUniquCode] = useState('');
  const [submitTrigger, setSubmitTrigger] = useState<number | null>(null);
  const [isSubmittingItems, setIsSubmittingItems] = useState(false);
  const navigate = useNavigate();
  const [previousSalesInvoice, setPreviousSalesInvoice] = useState<SalesInvoice | null>(null);

  const getPaymentMethods = async () => {
    try {
      const res = await axiosInstance.get(`/api/v1/paymentMethods`);
      setPaymentMethods(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getSalesStaff = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/users?role=1`
      );
      setSalesStaff(res.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  };

  const handleSubmitSaleInvoice = async () => {
    setIsLoading(true);
    const formattedSalesInvoice = {
      soldTo: salesInvoice.soldTo,
      customerId: salesInvoice.customerId,
      invoicedOn: salesInvoice.invoicedOn,
      invoiceNumber: salesInvoice.invoiceNumber,
      remarks: salesInvoice.remarks,
      paymentMethodId: salesInvoice.paymentMethodId,
      paymentReference: salesInvoice.paymentReference,
      salesStaffId: salesInvoice.salesStaffId,
      discountCode: salesInvoice.discountCode
    };
    try {
      const res = await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices`,
        formattedSalesInvoice
      );
      toast.success('Sales Invoice Created', {
        ...toastConfig,
        autoClose: 2000
      });
      setIsLoading(false);
      setIsSalesInvoiceCreated(true);
      setSalesInvoiceId(res.data.salesInvoiceId);
      setUniquCode(res.data.uniqueCode);
      
      // Update discountCode from the response
      if (res.data.discountCode) {
        setSalesInvoice(prev => ({
          ...prev,
          discountCode: res.data.discountCode
        }));
      }
      
      // Ensure we have at least one default empty item in the invoice
      if (items.length === 0 || (items.length === 1 && !items[0].productId && !items[0].noIdProductName)) {
        setItems([
          {
            productId: 0,
            procurementInvoiceId: 0,
            quantity: 1,
            unitOfMeasure: UnitOfMeasure.Piece,
            unitPrice: 0,
            productName: '',
            total: 0,
            salesInvoiceItemId: 0,
            noIdProductName: '',
            id: '',
            isDiscountable: false
          }
        ]);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
        setIsLoading(false);
      }
    }
  };

  const handleSubmitSalesInvoiceItems = async () => {
    // Prevent multiple simultaneous submissions
    if (isSubmittingItems) {
      console.log('Already submitting items, skipping...');
      return;
    }

    setIsSubmittingItems(true);
    setIsLoading(true);

    if (!selectedBranch?.branchDomain) {
      toast.error('Branch domain is missing');
      setIsLoading(false);
      setIsSubmittingItems(false);
      return;
    }

    const itemsToSubmit = items.filter(
      item => !item.isSubmitted && (item.productId || item.noIdProductName)
    );

    if (itemsToSubmit.length === 0) {
      setIsLoading(false);
      setIsSubmittingItems(false);
      return;
    }

    try {
      for (const item of itemsToSubmit) {
        const res = await axiosInstance.post(
          `/api/v1/${selectedBranch.branchDomain}/salesInvoices/${salesInvoiceId}/invoiceItems`,
          buildUpsertSalesInvoiceItemPayload(item)
        );
        const { salesInvoiceItemId } = res.data;
        setItems(prevItems => {
          const updatedItems = prevItems.map(i =>
            i.id === item.id
              ? {
                  ...i,
                  isSubmitted: true,
                  salesInvoiceItemId
                }
              : i
          );
          return updatedItems;
        });
      }
      toast.success('Sales Invoice Items Added', {
        ...toastConfig,
        autoClose: 2000
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.errorMessage || 'Something went wrong';
        toast.error(errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    } finally {
      setIsLoading(false);
      setIsSubmittingItems(false);
    }
  };

  const saveEditedInvoice = async () => {
    if (!isSalesInvoiceCreated) return;

    // Check if there are any actual changes
    if (previousSalesInvoice) {
      const hasChanges = Object.keys(salesInvoice).some(key => {
        const typedKey = key as keyof SalesInvoice;
        // Exclude discountCode from the comparison since it's handled separately
        if (typedKey === 'discountCode') return false;
        return salesInvoice[typedKey] !== previousSalesInvoice[typedKey];
      });

      if (!hasChanges) {
        return; // No changes, don't make the API call
      }
    }

    const formattedSalesInvoice = {
      soldTo: salesInvoice.soldTo,
      customerId: salesInvoice.customerId,
      invoicedOn: salesInvoice.invoicedOn,
      invoiceNumber: salesInvoice.invoiceNumber,
      remarks: salesInvoice.remarks,
      paymentMethodId: salesInvoice.paymentMethodId,
      paymentReference: salesInvoice.paymentReference,
      salesStaffId: salesInvoice.salesStaffId
    };

    try {
      const res = await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}`,
        formattedSalesInvoice
      );
      
      // Update the previous state after successful save
      setPreviousSalesInvoice({...salesInvoice});

      // Update discount code if it's in the response
      if (res.data.discountCode) {
        setSalesInvoice(prev => ({
          ...prev,
          discountCode: res.data.discountCode
        }));
      }
      
      toast.success('Sales Invoice Updated', {
        ...toastConfig,
        autoClose: 2000
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
        setIsLoading(false);
      }
    }
  };

  const handleSubmitSales = async () => {
    setIsLoading(true);
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}/submit`
      );
      toast.success('Sales Invoice Submitted', {
        ...toastConfig,
        autoClose: 2000
      });
      setIsLoading(false);
      handleResetForm();
      setIsSalesInvoiceCreated(false);
      navigate(`/sales-details/${salesInvoiceId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.errorMessage || 'Something went wrong';
        toast.error(errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
        setIsLoading(false);
      }
    }
  };

  const handleResetForm = () => {
    setSalesInvoice({
      soldTo: 'Cash',
      customerId: null,
      invoicedOn: new Date(),
      invoiceNumber: '',
      remarks: '',
      paymentMethodId: PaymentMethod.Cash,
      paymentReference: '',
      salesStaffId: null,
      customerName: '',
      discountCode: ''
    });
    setItems([
      {
        productId: 0,
        procurementInvoiceId: 0,
        quantity: 1,
        unitOfMeasure: UnitOfMeasure.Piece,
        unitPrice: 0,
        productName: '',
        total: 0,
        salesInvoiceItemId: 0,
        noIdProductName: '',
        isDiscountable: false
      }
    ]);
  };

  const getSalesInvoiceItems = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}/invoiceItems`
      );
      
      // If the response is empty, ensure we add a default empty item
      if (response.data.length === 0) {
        setItems([
          {
            productId: 0,
            procurementInvoiceId: 0,
            quantity: 1,
            unitOfMeasure: UnitOfMeasure.Piece,
            unitPrice: 0,
            productName: '',
            total: 0,
            salesInvoiceItemId: 0,
            noIdProductName: '',
            id: '',
            isDiscountable: false
          }
        ]);
      } else {
        setItems(mapApiSalesInvoiceItems(response.data));
      }
    } catch (error) {
      console.error('Error fetching invoice items:', error);
    }
  };

  useEffect(() => {
    getPaymentMethods();
    getSalesStaff();
  }, []);

  useEffect(() => {
    getSalesStaff();
  }, [selectedBranch]);

  useEffect(() => {
    if (submitTrigger !== null) {
      // Add a small delay to debounce rapid successive triggers
      const timeoutId = setTimeout(() => {
        handleSubmitSalesInvoiceItems(); // Call submit function when trigger is set
        setSubmitTrigger(null);
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [submitTrigger]);

  // Fetch invoice items when customer changes
  useEffect(() => {
    if (isSalesInvoiceCreated && salesInvoiceId && selectedBranch?.branchDomain) {
      const fetchInvoiceItems = async () => {
        try {
          const response = await axiosInstance.get(
            `/api/v1/${selectedBranch.branchDomain}/salesInvoices/${salesInvoiceId}/invoiceItems`
          );
          
          // If the response is empty, ensure we add a default empty item
          if (response.data.length === 0) {
            setItems([
              {
                productId: 0,
                procurementInvoiceId: 0,
                quantity: 1,
                unitOfMeasure: UnitOfMeasure.Piece,
                unitPrice: 0,
                productName: '',
                total: 0,
                salesInvoiceItemId: 0,
                noIdProductName: '',
                id: '',
                isDiscountable: false
              }
            ]);
          } else {
            setItems(mapApiSalesInvoiceItems(response.data));
          }
        } catch (error) {
          console.error('Error fetching invoice items:', error);
        }
      };
      
      // Add a small delay to ensure that the previous save operation has completed
      const timeoutId = setTimeout(() => {
        fetchInvoiceItems();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [salesInvoice.customerId, salesInvoice.soldTo, salesInvoice.discountCode, salesInvoiceId, isSalesInvoiceCreated, selectedBranch]);

  // Initialize previousSalesInvoice when salesInvoice is first loaded
  useEffect(() => {
    if (salesInvoice && !previousSalesInvoice) {
      setPreviousSalesInvoice({...salesInvoice});
    }
  }, [salesInvoice]);

  // Reset previousSalesInvoice when creating a new invoice
  useEffect(() => {
    if (!isSalesInvoiceCreated) {
      setPreviousSalesInvoice(null);
    }
  }, [isSalesInvoiceCreated]);

  return (
    <>
      <div className="d-flex flex-column">
        <Form className="mb-3">
          <CreateSalesInvoiceInputs
            salesInvoice={salesInvoice}
            setSalesInvoice={setSalesInvoice}
            salesStaff={salesStaff}
            paymentMethods={paymentMethods}
            uniqueCode={uniqueCode}
            isSalesInvoiceCreated={isSalesInvoiceCreated}
            saveEditedInvoice={saveEditedInvoice}
            salesInvoiceId={salesInvoiceId}
            getSalesInvoiceItems={getSalesInvoiceItems}
          />
        </Form>
        <Button
          className="align-self-end mb-2"
          onClick={handleSubmitSaleInvoice}
          disabled={isLoading}
          hidden={isSalesInvoiceCreated}
        >
          Create
        </Button>
      </div>
      <CreateSalesInvoiceTable
        items={items}
        setItems={setItems}
        salesInvoiceId={salesInvoiceId}
        isSalesInvoiceCreated={isSalesInvoiceCreated}
        isLoading={isLoading}
        handleSubmitSales={handleSubmitSales}
        setSubmitTrigger={setSubmitTrigger}
        isSubmittingItems={isSubmittingItems}
      />
    </>
  );
};

export default SalesInvoiceForm;
